import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";

/** Distance (px) a drag has to travel before it counts as a slide change. */
const SWIPE_THRESHOLD_PX = 48;

export interface CarouselStep {
  id: string;
  /** Raw `<svg>` markup; paints resolve against the `--cdh-*` tokens. */
  illustration: string;
  title: string;
  body: string;
  /** Verbatim line the user reads out to the cashier, when the step has one. */
  script?: string;
  /** Optional modifier for `.cdh-slide-art` when an illustration needs different sizing. */
  artClassName?: string;
}

interface StepsCarouselProps {
  steps: CarouselStep[];
  /** Milliseconds a slide stays on screen before advancing on its own. */
  intervalMs?: number;
  stepLabel: (index: number, total: number) => string;
  goToLabel: (index: number) => string;
  regionLabel: string;
}

/**
 * Horizontal step carousel: advances on its own until the user takes over.
 *
 * A swipe, a timer-segment tap or an arrow key stops the autoplay for good —
 * resuming it would yank the slide out from under someone who is still reading.
 */
export const StepsCarousel = memo(
  ({
    steps,
    intervalMs = 5000,
    stepLabel,
    goToLabel,
    regionLabel,
  }: StepsCarouselProps) => {
    const [index, setIndex] = useState(0);
    const [autoplay, setAutoplay] = useState(true);
    /** Live finger offset as a share of one slide, so the track follows the
     *  thumb. Kept in percent because the track is laid out in percent. */
    const [dragPercent, setDragPercent] = useState(0);
    /** Same offset in px — that is what the swipe threshold is expressed in. */
    const dragDistance = useRef(0);
    const dragStart = useRef<{ x: number; y: number } | null>(null);
    /** Set once the gesture is confidently horizontal — a vertical drag has to
     *  stay with the page scroll. */
    const dragging = useRef(false);

    const total = steps.length;

    const takeOver = useCallback(() => setAutoplay(false), []);

    const ticking =
      autoplay &&
      total >= 2 &&
      typeof window !== "undefined" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    useEffect(() => {
      if (!ticking) return;
      const advance = window.setInterval(
        () => setIndex((current) => (current + 1) % total),
        intervalMs,
      );
      return () => window.clearInterval(advance);
    }, [ticking, intervalMs, total, index]);

    const goTo = useCallback(
      (next: number) => {
        takeOver();
        setIndex(Math.min(Math.max(next, 0), total - 1));
      },
      [takeOver, total],
    );

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
      // Trackpad/mouse wheel momentum arrives as pointer events too; only a
      // real press starts a drag.
      if (event.button !== 0) return;
      dragStart.current = { x: event.clientX, y: event.clientY };
      dragging.current = false;
    };

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = dragStart.current;
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;

      if (!dragging.current) {
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
        dragging.current = true;
        takeOver();
        // Keeps the drag alive if the finger leaves the track.
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      // Rubber-band at both ends so the first/last slide feels like a wall.
      const atEdge = (dx > 0 && index === 0) || (dx < 0 && index === total - 1);
      const offset = atEdge ? dx * 0.25 : dx;
      dragDistance.current = offset;
      // A slide is exactly one viewport wide, so this is the whole conversion.
      setDragPercent((offset / (event.currentTarget.clientWidth || 1)) * 100);
    };

    const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
      const wasDragging = dragging.current;
      const offset = dragDistance.current;
      dragStart.current = null;
      dragging.current = false;
      dragDistance.current = 0;
      setDragPercent(0);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (!wasDragging || Math.abs(offset) < SWIPE_THRESHOLD_PX) return;
      setIndex((current) =>
        Math.min(Math.max(current + (offset < 0 ? 1 : -1), 0), total - 1),
      );
    };

    return (
      <section
        className="cdh-carousel"
        aria-roledescription="carousel"
        aria-label={regionLabel}
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") goTo(index + 1);
          if (event.key === "ArrowLeft") goTo(index - 1);
        }}
        tabIndex={0}
      >
        <div className="cdh-step-timer">
          <div
            className="cdh-step-timer-track"
            role="tablist"
            aria-label={regionLabel}
          >
            {steps.map((step, position) => {
              const isCurrent = position === index;
              const fill =
                position < index ? 1 : position > index ? 0 : ticking ? null : 1;
              return (
                <button
                  key={step.id}
                  type="button"
                  role="tab"
                  aria-selected={isCurrent}
                  aria-label={goToLabel(position)}
                  className={`cdh-step-timer-seg${isCurrent ? " cdh-step-timer-seg--active" : ""}`}
                  onClick={() => goTo(position)}
                >
                  <span className="cdh-step-timer-rail" aria-hidden>
                    <span
                      key={`${step.id}-${index}-${ticking ? "live" : "still"}`}
                      className={`cdh-step-timer-fill${fill === null ? " cdh-step-timer-fill--filling" : ""}`}
                      style={
                        fill === null
                          ? ({ "--cdh-step-ms": `${intervalMs}ms` } as CSSProperties)
                          : { width: `${fill * 100}%` }
                      }
                    />
                  </span>
                </button>
              );
            })}
          </div>
          <p className="cdh-step-timer-label">{stepLabel(index, total)}</p>
        </div>

        <div
          className="cdh-carousel-viewport"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            className={`cdh-carousel-track${dragPercent ? " cdh-carousel-track--dragging" : ""}`}
            style={{
              transform: `translate3d(${-index * 100 + dragPercent}%, 0, 0)`,
            }}
          >
            {steps.map((step, position) => (
              <article
                key={step.id}
                className="cdh-slide"
                role="group"
                aria-roledescription="slide"
                aria-label={stepLabel(position, total)}
                aria-hidden={position !== index}
              >
                <div
                  className={`cdh-slide-art${step.artClassName ? ` ${step.artClassName}` : ""}`}
                  aria-hidden
                  dangerouslySetInnerHTML={{ __html: step.illustration }}
                />
                <h3 className="cdh-slide-title">{step.title}</h3>
                <p className="cdh-slide-body">{step.body}</p>
                {step.script ? (
                  <p className="cdh-slide-script">{step.script}</p>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  },
);
